import React from 'react'

import { useTranslation } from 'next-i18next'

import {
  SEARCH_ENGINE_ICONS,
  IconType,
  getSearchIconByType
} from '@/constants/searchEngineAssets'

import CommonButton from '@/components/form/Button/CommonButton'

import CommonInput from '@/components/form/Input/CommonInput'

import { colors } from '@/styles/theme'

import BtnGlobalCustomStyles from '@/components/form/Button/button.customStyles'

import {
  RecentSearchesWrapper,
  RecentSearchesTitle,
  SearchItemsWrapper,
  SearchItem,
  Divider
} from './Options.styled'

interface SearchResult {
  id: string
  label: string
  value: [number, number]
  isAirport: boolean
  isRecentSearch?: boolean
}

interface SearchContentProps {
  settedValue: string
  setSettedValue: (value: string) => void
  isOpen: boolean
  filteredRecentSearches: SearchResult[]
  recentSearches: SearchResult[]
  options: SearchResult[]
  handleSelect: (option: SearchResult) => void
  removeRecentSearch: (id: string) => void
  clearHistory: () => void
}

const SearchContent: React.FC<SearchContentProps> = ({
  settedValue,
  setSettedValue,
  isOpen,
  filteredRecentSearches,
  recentSearches,
  options,
  handleSelect,
  removeRecentSearch,
  clearHistory
}) => {
  const { t } = useTranslation('parkings')

  const highlightMatch = (text: string, highlight: string) => {
    if (!highlight) return text

    const regex = new RegExp(`(${highlight})`, 'i') // 'i' flag to handle also capital and non-capital letters
    const parts = text.split(regex)

    return parts.map((part, index) =>
      regex.test(part) ? <strong key={index}>{part}</strong> : part
    )
  }

  return (
    <>
      <CommonInput
        variant="search"
        type="text"
        value={settedValue}
        onChange={e => setSettedValue(e.target.value)}
        placeholder={t('searchNearParking.search.placeholder')}
      />
      {settedValue === '' && recentSearches.length > 0 && (
        <>
          <RecentSearchesWrapper>
            <RecentSearchesTitle>
              {t('searchNearParking.search.recentSearches')}
            </RecentSearchesTitle>
            <CommonButton
              variant="options"
              onClick={clearHistory}
              customStyle={BtnGlobalCustomStyles.mobileSearchClearHistory}
            >
              {t('searchNearParking.search.clearHistory')}
            </CommonButton>
          </RecentSearchesWrapper>
          <SearchItemsWrapper>
            {recentSearches.map((item: SearchResult, index: number) => (
              <SearchItem key={index} onClick={() => handleSelect(item)}>
                <img
                  src={getSearchIconByType(IconType.RECENT_SEARCH)}
                  alt="recent search icon"
                />
                <span>{highlightMatch(item.label, settedValue)}</span>
                <button onClick={() => removeRecentSearch(item.id)}>
                  <img
                    src={SEARCH_ENGINE_ICONS.deleteRecentSearch}
                    alt="delete icon"
                  />
                </button>
              </SearchItem>
            ))}
          </SearchItemsWrapper>
        </>
      )}
      {isOpen && settedValue !== '' && (
        <>
          {filteredRecentSearches.length > 0 && (
            <>
              <SearchItemsWrapper>
                {filteredRecentSearches.map(
                  (item: SearchResult, index: number) => (
                    <SearchItem key={index} onClick={() => handleSelect(item)}>
                      <img
                        src={getSearchIconByType(IconType.RECENT_SEARCH)}
                        alt="recent search icon"
                      />
                      <span>{highlightMatch(item.label, settedValue)}</span>
                      <button onClick={() => removeRecentSearch(item.id)}>
                        <img
                          src={SEARCH_ENGINE_ICONS.deleteRecentSearch}
                          alt="delete icon"
                        />
                      </button>
                    </SearchItem>
                  )
                )}
              </SearchItemsWrapper>
              <Divider />
            </>
          )}
          <SearchItemsWrapper>
            {options.map((item: SearchResult, index: number) => (
              <SearchItem key={index} onClick={() => handleSelect(item)}>
                <img
                  src={getSearchIconByType(
                    item.isAirport ? IconType.AIRPORT : IconType.LOCATION
                  )}
                  alt="location icon"
                />
                <span>{highlightMatch(item.label, settedValue)}</span>
                {!item.isRecentSearch && (
                  <button style={{ visibility: 'hidden' }}>X</button>
                )}
              </SearchItem>
            ))}
          </SearchItemsWrapper>
        </>
      )}
    </>
  )
}

export default SearchContent
